import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { RestorePlanDetail } from '../api/types';

export function RestoreApproval() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const [plan, setPlan] = useState<RestorePlanDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [isActioning, setIsActioning] = useState(false);

  useEffect(() => {
    if (!planId) return;
    let cancelled = false;

    async function fetchPlan() {
      try {
        const data = await api.get<RestorePlanDetail>(`/restore-plans/${planId}`);
        if (!cancelled) {
          setPlan(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load plan');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchPlan();
    return () => { cancelled = true; };
  }, [planId]);

  async function handleApprove() {
    if (!planId || !plan) return;
    setIsActioning(true);
    setError(null);

    try {
      await api.post(`/restore-plans/${planId}/approve`, { checksum: plan.checksum });
      navigate(`/restore/execution/${planId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approval failed');
    } finally {
      setIsActioning(false);
    }
  }

  async function handleReject() {
    if (!planId || !rejectionReason.trim()) return;
    setIsActioning(true);
    setError(null);

    try {
      await api.post(`/restore-plans/${planId}/reject`, { reason: rejectionReason });
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rejection failed');
    } finally {
      setIsActioning(false);
    }
  }

  if (isLoading) {
    return (
      <div role="status" aria-live="polite">
        <h2>Restore Approval</h2>
        <p>Loading plan details…</p>
      </div>
    );
  }

  if (error && !plan) {
    return (
      <div role="alert">
        <h2>Restore Approval</h2>
        <div className="card" style={{ borderLeft: '4px solid var(--color-error)' }}>
          <p style={{ color: 'var(--color-error)' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!plan) return null;

  const isPending = plan.status === 'PENDING_APPROVAL';

  return (
    <div>
      <h2>Restore Plan Approval</h2>

      {error && (
        <div className="card" role="alert" style={{ borderLeft: '4px solid var(--color-error)', marginBottom: '1rem' }}>
          <p style={{ color: 'var(--color-error)', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Plan Details */}
      <div className="card">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <div className="form-group">
              <label>Plan ID</label>
              <p style={{ margin: 0 }}><code>{plan.planId}</code></p>
            </div>
            <div className="form-group">
              <label>Status</label>
              <p style={{ margin: 0 }}>
                <span className={`badge ${plan.status === 'APPROVED' ? 'badge-success' : plan.status === 'REJECTED' ? 'badge-error' : 'badge-warning'}`}>
                  {plan.status.replace('_', ' ')}
                </span>
              </p>
            </div>
            <div className="form-group">
              <label>Records to Restore</label>
              <p style={{ margin: 0, fontWeight: 600, fontSize: '1.25rem' }}>{plan.recordCount}</p>
            </div>
          </div>
          <div>
            <div className="form-group">
              <label>Target Organization</label>
              <p style={{ margin: 0 }}>{plan.targetOrg.name} ({plan.targetOrg.environment})</p>
              <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>{plan.targetOrg.instanceUrl}</p>
            </div>
            <div className="form-group">
              <label>Requested By</label>
              <p style={{ margin: 0 }}>{plan.createdBy}</p>
            </div>
            <div className="form-group">
              <label>Created At</label>
              <p style={{ margin: 0 }}>{new Date(plan.createdAt).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Immutable Plan Checksum */}
      <section aria-labelledby="checksum-heading" style={{ marginTop: '1.5rem' }}>
        <h3 id="checksum-heading">Plan Integrity</h3>
        <div className="card" style={{ background: '#f8f9fa' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>SHA-256 Checksum</label>
            <code
              style={{
                display: 'block',
                padding: '0.5rem',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                fontSize: '0.8rem',
                wordBreak: 'break-all',
                fontFamily: 'monospace',
              }}
              aria-label="Plan checksum"
            >
              {plan.checksum}
            </code>
            <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.25rem', marginBottom: 0 }}>
              This checksum verifies the plan has not been modified since creation.
            </p>
          </div>
        </div>
      </section>

      {/* Records in Plan */}
      <section aria-labelledby="records-heading" style={{ marginTop: '1.5rem' }}>
        <h3 id="records-heading">Records ({plan.records.length})</h3>
        <div style={{ maxHeight: '300px', overflow: 'auto' }}>
          <table aria-label="Records in restore plan">
            <thead>
              <tr>
                <th scope="col">Object Type</th>
                <th scope="col">Record ID</th>
                <th scope="col">Name</th>
              </tr>
            </thead>
            <tbody>
              {plan.records.map((rec) => (
                <tr key={rec.recordId}>
                  <td><span className="badge badge-info">{rec.objectType}</span></td>
                  <td><code style={{ fontSize: '0.8rem' }}>{rec.recordId}</code></td>
                  <td>{rec.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Approval Actions */}
      {isPending && (
        <section aria-labelledby="actions-heading" style={{ marginTop: '2rem' }}>
          <h3 id="actions-heading">Approval Decision</h3>
          <div className="card">
            {!showRejectForm ? (
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  className="success"
                  onClick={handleApprove}
                  disabled={isActioning}
                  aria-label="Approve restore plan"
                >
                  {isActioning ? 'Processing…' : '✓ Approve & Proceed'}
                </button>
                <button
                  className="danger"
                  onClick={() => setShowRejectForm(true)}
                  disabled={isActioning}
                  aria-label="Reject restore plan"
                >
                  ✗ Reject
                </button>
              </div>
            ) : (
              <div>
                <div className="form-group">
                  <label htmlFor="rejectReason">Rejection Reason</label>
                  <textarea
                    id="rejectReason"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Provide a reason for rejection…"
                    rows={3}
                    required
                    aria-required="true"
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    className="danger"
                    onClick={handleReject}
                    disabled={isActioning || !rejectionReason.trim()}
                  >
                    {isActioning ? 'Rejecting…' : 'Confirm Rejection'}
                  </button>
                  <button
                    className="secondary"
                    onClick={() => setShowRejectForm(false)}
                    disabled={isActioning}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Already actioned */}
      {plan.status === 'APPROVED' && (
        <div className="card" style={{ marginTop: '1.5rem', borderLeft: '4px solid var(--color-success)' }}>
          <p style={{ margin: 0 }}>
            ✓ Approved by <strong>{plan.approvedBy}</strong> on{' '}
            {plan.approvedAt ? new Date(plan.approvedAt).toLocaleString() : 'N/A'}
          </p>
          <button
            onClick={() => navigate(`/restore/execution/${plan.planId}`)}
            style={{ marginTop: '0.75rem' }}
          >
            View Execution
          </button>
        </div>
      )}

      {plan.status === 'REJECTED' && (
        <div className="card" style={{ marginTop: '1.5rem', borderLeft: '4px solid var(--color-error)' }}>
          <p style={{ margin: 0 }}>
            ✗ Rejected{plan.rejectionReason && `: ${plan.rejectionReason}`}
          </p>
        </div>
      )}
    </div>
  );
}
